import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { useCampusStore } from '@/stores/campus'

/**
 * Guide-mode helpers for campus routes (/curriculum/*, /tracks/*) when ?userId= is present.
 * Target user + fellow profile are synced from CampusLayout (watch on route query).
 */
export function useGuideMode() {
  const route = useRoute()
  const campusStore = useCampusStore()

  const guideTargetUserId = computed(() => route.query.userId as string | undefined)
  const guidePodId = computed(() => route.query.podId as string | undefined)
  const guideFellowName = computed(() => route.query.fellowName as string | undefined)
  const isGuideMode = computed(() => !!guideTargetUserId.value)

  const guideFellowDisplayName = computed(() => {
    const fromApi = campusStore.guideFellowDisplay?.displayName
    if (fromApi) return fromApi
    if (guideFellowName.value?.trim()) return guideFellowName.value.trim()
    if (guideTargetUserId.value) return `User ${guideTargetUserId.value}`
    return ''
  })

  const guideFellowAvatarUrl = computed(() => campusStore.guideFellowDisplay?.avatarUrl ?? null)

  /** URL to return to the fellow's manage page */
  const backToFellowUrl = computed(() => {
    if (!guideTargetUserId.value) return '/manage'
    if (guidePodId.value) return `/manage/pod/${guidePodId.value}/${guideTargetUserId.value}`
    return `/manage/campus/fellows/${guideTargetUserId.value}`
  })

  /** Base query object for router.push when navigating between guide-mode pages */
  const guideQuery = computed(() => {
    const q: Record<string, string> = {}
    if (guideTargetUserId.value) q.userId = guideTargetUserId.value
    if (guidePodId.value) q.podId = guidePodId.value
    if (guideFellowName.value) q.fellowName = guideFellowName.value
    return q
  })

  /** Use for sidebar / in-page links so guide mode keeps ?userId=… on campus routes */
  function campusLink(path: string): RouteLocationRaw {
    const q = guideQuery.value
    if (Object.keys(q).length === 0) return path
    return { path, query: { ...q } }
  }

  return {
    guideTargetUserId,
    guidePodId,
    guideFellowName,
    guideFellowDisplayName,
    guideFellowAvatarUrl,
    isGuideMode,
    backToFellowUrl,
    guideQuery,
    campusLink,
  }
}
